// @owners { team: patients }
import { type SurveyResponse } from '@alto/scriptdash/alto/surveys/types/v1/survey_response';
import { type QueryClient } from '@tanstack/react-query';
import { queries } from '~shared/queries/query-keys';
import { Sentry } from '~shared/sentry';
import { type APIError } from '~shared/types';

const getErrorStatusCode = (error: unknown): number | undefined =>
  error && typeof error === 'object' && 'details' in error ? (error as APIError).details?.statusCode : undefined;

// 401 (expired/missing session) and 598 (network) are expected for this opportunistic
// survey check — hide the modal, don't page Sentry.
const isExpectedRateShipmentFetchError = (error: unknown): boolean => {
  const statusCode = getErrorStatusCode(error);
  return statusCode === 401 || statusCode === 598;
};

/**
 * Imperatively fetch the pending "rate shipment" survey via React Query — for the non-React call
 * sites (native app-open middleware, web post-auth hook) that can't use the useGetPendingRateShipment
 * hook. Returns the pending survey, or null if there is none / on error (so the modal stays hidden).
 */
export const fetchPendingRateShipment = async (queryClient: QueryClient): Promise<SurveyResponse | null> => {
  try {
    return (await queryClient.fetchQuery(queries.rateShipment.pending)) ?? null;
  } catch (error) {
    if (!isExpectedRateShipmentFetchError(error)) {
      Sentry.captureException(error instanceof Error ? error : new Error('fetchPendingRateShipment failed'));
    }
    return null;
  }
};
